import {
  AnalyticsEvents,
  setIsOverlayLogin,
  translateSubscriptionFeature,
  useApp,
  useAppDispatch,
  useTranslation,
} from "@next-platform/client-common-core";
import { Icon, IconSize, IconVariants } from "@next-platform/client-common-spicy-ui";
import { Spacer, Table, TableBody, TableCell, TableColumn, TableHeader, TableRow } from "@nextui-org/react";
import { useMemo } from "react";
import { useNavigate } from "react-router-dom";

import { Col, Row, SpicyButton, Text } from "@/components/customComponents";
import { LocalizedLink } from "@/components/LocalizedLink/LocalizedLink";
import Trans from "@/components/Trans/Trans";
import {
  POPULAR_PLAN,
  SUBSCRIBE_USING_CRYPTO_PAGE_ROUTE,
  SUBSCRIPTION_BILLING_PERIOD,
  SUBSCRIPTION_BILLING_PERIOD_TYPE,
  USER_ROLES,
} from "@/constants";
import { useMediaStore, useQueueLogEvent } from "@/hooks";
import useLocalizedLocation from "@/hooks/useLocalizedLocation";
import useAuth from "@/services/useAuth";
import { IPlanData, PlanComparisonTableProps } from "@/types";
import { cn, getCookie, getCryptoPaymentLink, getCurrencySymbol, getPurchaseData, onSubscribeSticky } from "@/utils";
import { getSubscriptionFeatures } from "@/utils/getSubscriptionFeatures";

import { ISubscriptionFeatureData, SubscriptionFeatureValue } from "../types";
import getSubscriptionCardLabel from "../utils/getSubscriptionCardLabel";
import getSubscriptionComparisonFeatures from "../utils/getSubscriptionComparisonFeatures";

const COUNTRY = getCookie("country");

const tableClassNames = {
  thead: "[&>tr:last-child]:hidden",
  table: "border-spacing-0 text-center",
};

const resolveFallbackBillingPeriod = (
  frequency: PlanComparisonTableProps["frequency"],
): (typeof SUBSCRIPTION_BILLING_PERIOD)[keyof typeof SUBSCRIPTION_BILLING_PERIOD] => {
  if (frequency === SUBSCRIPTION_BILLING_PERIOD_TYPE.ANNUAL) {
    return SUBSCRIPTION_BILLING_PERIOD.ANNUAL;
  }
  if (frequency === SUBSCRIPTION_BILLING_PERIOD_TYPE.QUARTERLY) {
    return SUBSCRIPTION_BILLING_PERIOD.QUARTERLY;
  }
  return SUBSCRIPTION_BILLING_PERIOD.MONTHLY;
};

const getIsSubscribeDisabled = ({
  isSubsribeUsingCryptoPage,
  isMinimalTier,
  isMonthly,
}: {
  isSubsribeUsingCryptoPage: boolean;
  isMinimalTier: boolean;
  isMonthly: boolean;
}) => isSubsribeUsingCryptoPage && isMinimalTier && isMonthly;

export default function PlanComparisonTable({ frequency, tabledata }: PlanComparisonTableProps) {
  const { applicationName } = useApp();
  const { isAuthenticated } = useAuth();
  const { t } = useTranslation();
  const subscriptionFeatures = getSubscriptionFeatures(tabledata);
  const { logEvent } = useQueueLogEvent();
  const subscriptionComparisonData = [
    ...getSubscriptionComparisonFeatures(t),
    ...subscriptionFeatures.map((row) => ({
      ...row,
      feature: translateSubscriptionFeature({ feature: row.feature, t }),
    })),
  ];

  const columns = useMemo(
    () => [
      {
        role: "feature",
      },
      ...(tabledata || []),
    ],
    [tabledata, t],
  );

  const navigate = useNavigate();
  const { pagePath } = useLocalizedLocation();
  const isSubsribeUsingCryptoPage = pagePath === SUBSCRIBE_USING_CRYPTO_PAGE_ROUTE;
  const dispatch = useAppDispatch();
  const isMobile = useMediaStore((screen) => screen.isMobile);
  const fallbackBillingPeriod = resolveFallbackBillingPeriod(frequency);

  const renderHeadCell = (tabledata: IPlanData) => {
    const { tier, price } = getPurchaseData(tabledata);
    const role = tabledata.role;
    const isPopular = role === POPULAR_PLAN;
    const isFeatureColoumn = role === "feature";
    const period = tabledata.period;
    const isMonthly = period === SUBSCRIPTION_BILLING_PERIOD.MONTHLY;
    const isMinimalTier = role === USER_ROLES.MINIMAL;
    const planId = tabledata.plan_id;
    const frequencyLabel = getSubscriptionCardLabel(period || fallbackBillingPeriod, t);
    const isSubscribeDisabled = getIsSubscribeDisabled({ isSubsribeUsingCryptoPage, isMinimalTier, isMonthly });
    const currencyCode = tabledata.currency || COUNTRY;
    const currencySymbol = getCurrencySymbol(currencyCode);
    const isCryptoLink = isSubsribeUsingCryptoPage;

    const handleSubscriptionClick = (argPlanId: string, coupon = "") => {
      if (!isAuthenticated) {
        return dispatch(setIsOverlayLogin(true));
      }

      logEvent(AnalyticsEvents.SUBSCRIPTION_PLAN_SELECT, { plan_id: planId, plan_name: tier });
      logEvent(AnalyticsEvents.SUBSCRIPTION_MONEY_VALUE, { currency: currencyCode, value: price });

      if (isSubsribeUsingCryptoPage) {
        return;
      }
      const route = onSubscribeSticky(argPlanId, coupon);
      navigate(route);
    };

    return (
      <TableColumn
        key={role}
        className={cn(
          "border-0 border-b-1 border-t-1 first:border-l-1 !border-l-1 last:border-r-1 last:border-l-0 border-solid border-opacity-50 relative p-2 px-4 !rounded-none",
          isPopular ? "!border-blue-800 bg-gray-50 !border-x-2" : "border-foreground bg-transparent",
        )}
      >
        {isPopular ? (
          <div className="absolute -translate-y-full border-2 border-solid w-full text-center top-0 -left-0.5 bg-blue-600 bg-opacity-50 border-blue-600 rounded-tl-xl rounded-tr-xl py-1 box-content">
            <Text type="normal white" variant="md">
              <Trans i18nKey="subscription:page.planComparisonTable.mostPopular" />
            </Text>
          </div>
        ) : null}
        <Col className="py-2">
          <Text b variant="lg" type="capitalize">
            {isFeatureColoumn ? <Trans i18nKey="subscription:page.planComparisonTable.feature" /> : tier}
          </Text>
          <Spacer y={4} />
          {isFeatureColoumn ? (
            <Col className="gap-3">
              <Text variant="lg">
                <Icon icon={IconVariants.CHECK} className="text-blue-600 relative top-0.5 mr-1" size={IconSize.LARGE} />{" "}
                <Trans i18nKey="subscription:page.planComparisonTable.heading.includeWithPlan" />
              </Text>
              <Text variant="lg">
                <Icon icon={IconVariants.CLOSE} className="relative top-0.5 mr-1" size={IconSize.LARGE} />{" "}
                <Trans i18nKey="subscription:page.planComparisonTable.heading.notIncludedWithPlan" />
              </Text>
            </Col>
          ) : (
            <Row>
              <Text b variant="lg">
                {currencySymbol}
                {price}
              </Text>
              <Text type="normal" variant="lg">
                /{frequencyLabel}
              </Text>
            </Row>
          )}
          <Spacer y={6} />
          {!isFeatureColoumn && (
            <SpicyButton
              as={LocalizedLink}
              to={isCryptoLink ? getCryptoPaymentLink(tabledata) : undefined}
              onClick={
                // eslint-disable-next-line react-perf/jsx-no-new-function-as-prop -- NextUI typings/usage forces an inline handler here
                () => handleSubscriptionClick(planId)
              }
              variant="primary_large"
              color="primary_medium"
              isDisabled={isSubscribeDisabled}
              target={isCryptoLink ? "_blank" : undefined}
            >
              <Trans i18nKey="subscription:page.subscriptionCard.buttonPaid" />
            </SpicyButton>
          )}
        </Col>
      </TableColumn>
    );
  };

  const renderDataCell = (feature: SubscriptionFeatureValue, key: string) => {
    const isFeatureCol = key === "feature";
    const isPopular = key === POPULAR_PLAN;

    let cellContent: string | number | null = null;
    if (isFeatureCol && typeof feature === "string") {
      cellContent = feature;
    } else if (!isFeatureCol && typeof feature === "number") {
      cellContent = feature;
    }

    const iconForBoolFeature = feature ? (
      <Icon icon={IconVariants.CHECK} className="text-blue-600" size={IconSize.SMALL} />
    ) : (
      <Icon icon={IconVariants.CLOSE} className="opacity-50" size={IconSize.SMALL} />
    );
    return (
      <TableCell
        key={key}
        className={
          "border-0 border-b-1 !border-l-1 last:border-r-1 last:border-l-0 border-solid border-opacity-50 text-center first:text-start whitespace-nowrap p-3 " +
          (isPopular ? "!border-blue-800 bg-gray-50 !border-x-2" : "border-foreground")
        }
      >
        {typeof feature !== "boolean" ? (
          <Text type={typeof feature === "number" ? "opacity-50" : undefined} variant="regular">
            {isFeatureCol ? <Icon icon={IconVariants.CHEVRON_RIGHT} className="text-blue-600 relative top-0.5" size={IconSize.SMALL} /> : null}{" "}
            {cellContent}
          </Text>
        ) : (
          <> {iconForBoolFeature} </>
        )}
      </TableCell>
    );
  };
  if (isMobile) return null;
  return (
    <>
      <Spacer y={14} />
      <Text b variant="big" type="text-center">
        <Trans i18nKey="subscription:page.planComparisonTable.compareFeaturesTitle" values={{ applicationName }} />
      </Text>
      <Spacer y={12} />
      <div className="overflow-x-auto w-full pt-8">
        <Table removeWrapper classNames={tableClassNames}>
          <TableHeader columns={columns}>{columns?.map((planData) => renderHeadCell(planData as IPlanData))}</TableHeader>
          <TableBody>
            {subscriptionComparisonData
              ?.filter(Boolean)
              .map((subscriptionData, idx) =>
                subscriptionData ? (
                  <TableRow key={idx}>
                    {columns.map((cellData) => renderDataCell(subscriptionData[cellData.role as keyof ISubscriptionFeatureData], cellData.role))}
                  </TableRow>
                ) : (
                  <></>
                ),
              )}
          </TableBody>
        </Table>
      </div>
    </>
  );
}
